import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { countyService, CountyInfo } from '../services/countyService';
import { teamService } from '../services/teamService';
import { Team } from '../types';
import { getLastLeagueMapPath } from '../utils/lastLeague';
import './CommandPalette.css';

type PaletteItem = {
    id: string;
    group: 'ask' | 'leagues' | 'counties' | 'teams' | 'pages';
    title: string;
    subtitle?: string;
    to: string;
};

const LEAGUES: { id: string; name: string; path: string }[] = [
    { id: 'ncaa_basketball', name: 'NCAA Basketball', path: '/league/ncaa_basketball' },
    { id: 'ncaa_football', name: 'NCAA Football', path: '/league/ncaa_football' },
    { id: 'nfl', name: 'NFL', path: '/league/nfl' },
    { id: 'nba', name: 'NBA', path: '/league/nba' },
    { id: 'nhl', name: 'NHL', path: '/league/nhl' },
    { id: 'mlb', name: 'MLB', path: '/league/mlb' },
    { id: 'mls', name: 'MLS', path: '/league/mls' },
    { id: 'ufc', name: 'UFC World Map', path: '/ufc' },
    { id: 'naming_wars', name: 'Soccer vs Football', path: '/naming-wars' },
];

const PAGES: { title: string; subtitle: string; path: string }[] = [
    { title: 'My County', subtitle: 'ZIP lookup & home turf', path: '/my-county' },
    { title: 'Play hub', subtitle: 'Survivor, fantasy, treasury', path: '/play' },
    { title: 'Leagues', subtitle: 'All territory maps', path: '/leagues' },
    { title: 'News', subtitle: 'Updates & community', path: '/news' },
    { title: 'Ask the Map', subtitle: 'Full natural-language stats', path: '/stats' },
];

const ASK_EXAMPLES = [
    'Which team holds the most counties this season?',
    'Who has conquered the most land in NCAA basketball?',
];

let countiesCache: CountyInfo[] | null = null;
let teamsCache: { leagueId: string; teams: Team[] } | null = null;

interface CommandPaletteContextValue {
    open: boolean;
    openPalette: () => void;
    closePalette: () => void;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

export function useCommandPalette(): CommandPaletteContextValue {
    const ctx = useContext(CommandPaletteContext);
    if (!ctx) throw new Error('useCommandPalette must be used within CommandPaletteProvider');
    return ctx;
}

/** Optional hook — returns null outside the provider (safe for optional chrome). */
export function useCommandPaletteOptional(): CommandPaletteContextValue | null {
    return useContext(CommandPaletteContext);
}

function norm(s: string): string {
    return s.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
}

function matches(hay: string, needle: string): boolean {
    if (!needle) return true;
    const h = norm(hay);
    const parts = norm(needle).split(' ');
    return parts.every((p) => h.includes(p));
}

export const CommandPaletteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [open, setOpen] = useState(false);
    const openPalette = useCallback(() => setOpen(true), []);
    const closePalette = useCallback(() => setOpen(false), []);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const metaK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
            const slash = e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey;
            if (!metaK && !slash) return;
            const tag = (e.target as HTMLElement)?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) {
                return;
            }
            e.preventDefault();
            setOpen(true);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const value = useMemo(
        () => ({ open, openPalette, closePalette }),
        [open, openPalette, closePalette],
    );

    return (
        <CommandPaletteContext.Provider value={value}>
            {children}
            <CommandPalette open={open} onClose={closePalette} />
        </CommandPaletteContext.Provider>
    );
};

const GROUP_LABEL: Record<PaletteItem['group'], string> = {
    ask: 'Ask the Map',
    leagues: 'Leagues',
    counties: 'Counties',
    teams: 'Teams',
    pages: 'Pages',
};

const CommandPalette: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
    const navigate = useNavigate();
    const inputRef = useRef<HTMLInputElement>(null);
    const [query, setQuery] = useState('');
    const [active, setActive] = useState(0);
    const [counties, setCounties] = useState<CountyInfo[]>([]);
    const [teams, setTeams] = useState<Team[]>([]);
    const [loadingIndex, setLoadingIndex] = useState(false);

    useEffect(() => {
        if (!open) {
            setQuery('');
            setActive(0);
            return;
        }
        const t = window.setTimeout(() => inputRef.current?.focus(), 30);
        return () => window.clearTimeout(t);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setLoadingIndex(true);

        (async () => {
            try {
                if (!countiesCache) {
                    countiesCache = await countyService.listCounties();
                }
                const leagueMatch = getLastLeagueMapPath().match(/^\/league\/([^/]+)/);
                const leagueId = leagueMatch?.[1] || 'ncaa_basketball';
                if (!teamsCache || teamsCache.leagueId !== leagueId) {
                    const res = await teamService.getTeamsByLeague(leagueId, true);
                    teamsCache = { leagueId, teams: res.teams || [] };
                }
                if (!cancelled) {
                    setCounties(countiesCache);
                    setTeams(teamsCache.teams);
                }
            } catch {
                if (!cancelled) {
                    setCounties(countiesCache || []);
                    setTeams(teamsCache?.teams || []);
                }
            } finally {
                if (!cancelled) setLoadingIndex(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open]);

    const items = useMemo(() => {
        const q = query.trim();
        const out: PaletteItem[] = [];

        if (q.length >= 2) {
            out.push({
                id: 'ask-query',
                group: 'ask',
                title: q,
                subtitle: 'Ask the Map — open full answer',
                to: `/stats?q=${encodeURIComponent(q)}`,
            });
        } else {
            ASK_EXAMPLES.forEach((ex, i) => {
                out.push({
                    id: `ask-${i}`,
                    group: 'ask',
                    title: ex,
                    subtitle: 'Suggested question',
                    to: `/stats?q=${encodeURIComponent(ex)}`,
                });
            });
        }

        LEAGUES.filter((l) => matches(`${l.name} ${l.id}`, q))
            .slice(0, 6)
            .forEach((l) => {
                out.push({
                    id: `league-${l.id}`,
                    group: 'leagues',
                    title: l.name,
                    subtitle: 'Open live map',
                    to: l.path,
                });
            });

        if (q.length >= 2) {
            counties
                .filter((c) => matches(`${c.county_name} ${c.state_name} ${c.county_slug} ${c.state_slug}`, q))
                .slice(0, 8)
                .forEach((c) => {
                    out.push({
                        id: `county-${c.county_fips}`,
                        group: 'counties',
                        title: `${c.county_name}, ${c.state_name}`,
                        subtitle: 'Who owns this county',
                        to: `/who-owns/${c.state_slug}/${c.county_slug}`,
                    });
                });

            teams
                .filter((t) => matches(`${t.team_name} ${t.short_name || ''}`, q))
                .slice(0, 8)
                .forEach((t) => {
                    const leagueId = teamsCache?.leagueId || 'ncaa_basketball';
                    out.push({
                        id: `team-${t.team_id}`,
                        group: 'teams',
                        title: t.team_name,
                        subtitle: `${leagueId.replace(/_/g, ' ')} · focus on map`,
                        to: `/league/${leagueId}?focusTeam=${t.team_id}`,
                    });
                });
        }

        PAGES.filter((p) => matches(`${p.title} ${p.subtitle}`, q))
            .slice(0, 5)
            .forEach((p) => {
                out.push({
                    id: `page-${p.path}`,
                    group: 'pages',
                    title: p.title,
                    subtitle: p.subtitle,
                    to: p.path,
                });
            });

        return out;
    }, [query, counties, teams]);

    useEffect(() => {
        setActive(0);
    }, [query, items.length]);

    const go = useCallback(
        (item: PaletteItem) => {
            onClose();
            navigate(item.to);
        },
        [navigate, onClose],
    );

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
            } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActive((i) => Math.min(i + 1, Math.max(0, items.length - 1)));
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActive((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Enter' && items[active]) {
                e.preventDefault();
                go(items[active]);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, items, active, go, onClose]);

    if (!open) return null;

    let lastGroup: PaletteItem['group'] | null = null;

    return (
        <div className="cmdk" role="dialog" aria-modal="true" aria-label="Search or ask the map">
            <button type="button" className="cmdk-backdrop" aria-label="Close" onClick={onClose} />
            <div className="cmdk-panel">
                <div className="cmdk-input-row">
                    <svg className="cmdk-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                        <circle cx="11" cy="11" r="6.5" />
                        <path d="m20 20-4.2-4.2" />
                    </svg>
                    <input
                        ref={inputRef}
                        className="cmdk-input"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Ask the Map, find a county or team…"
                        aria-autocomplete="list"
                        aria-controls="cmdk-list"
                    />
                    <kbd className="cmdk-kbd">esc</kbd>
                </div>
                <div className="cmdk-list" id="cmdk-list" role="listbox">
                    {loadingIndex && items.length === 0 && (
                        <div className="cmdk-empty">Loading search index…</div>
                    )}
                    {!loadingIndex && items.length === 0 && (
                        <div className="cmdk-empty">No matches. Try a county, team, or ask a question.</div>
                    )}
                    {items.map((item, index) => {
                        const showGroup = item.group !== lastGroup;
                        lastGroup = item.group;
                        return (
                            <React.Fragment key={item.id}>
                                {showGroup && (
                                    <div className="cmdk-group">{GROUP_LABEL[item.group]}</div>
                                )}
                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={index === active}
                                    className={`cmdk-item${index === active ? ' is-active' : ''}`}
                                    onMouseEnter={() => setActive(index)}
                                    onClick={() => go(item)}
                                >
                                    <span className="cmdk-item-text">
                                        <span className="cmdk-item-title">{item.title}</span>
                                        {item.subtitle && (
                                            <span className="cmdk-item-sub">{item.subtitle}</span>
                                        )}
                                    </span>
                                    <span className="cmdk-item-go">↵</span>
                                </button>
                            </React.Fragment>
                        );
                    })}
                </div>
                <div className="cmdk-footer">
                    <span>↑↓ navigate</span>
                    <span>↵ open</span>
                    <span>/ or ⌘K</span>
                </div>
            </div>
        </div>
    );
};

export default CommandPaletteProvider;
